import { v4 as uuidv4 } from "uuid";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getResourcesAsync } from "./resourceSlice";
import { returnDataArrayObject } from "../utils/arrayHelper";
import Axios from "axios";
import * as _ from "lodash";
import moment from "moment";

import jwt from "jsonwebtoken";
const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;
// const JWT_TOKEN = `Bearer ${localStorage.getItem("accessToken")}`;

// How many rows to inspect when working out the column list.
const FIELD_SAMPLE_ROWS = 50;

/**
 * Builds the column list from a resource's response.
 *
 * There is no schema anywhere — columns are the keys of the returned rows — so this is
 * the only place columns come into existence. The previous version read `data[0]` only
 * and kept a key only if that first value was a string/number/array, which silently
 * dropped columns in two very common cases:
 *
 *   1. sparse data — if row 0 has `Discount: null` the Discount column disappeared,
 *      even though every other row has a value. If most of row 0 is null you get data
 *      in the table but no columns at all.
 *   2. booleans — `typeof false === "boolean"`, so flags like IsDeleted were dropped.
 *
 * So: union the keys across a sample of rows, and decide the type from the first value
 * that is actually set. A column whose sampled values are all null is still a column.
 */
export const deriveAvailableFields = (reportData, options) => {
  const includeArrays = Boolean(options && options.includeArrays);
  const rows = (returnDataArrayObject(reportData) || {}).data || [];
  if (!rows.length) return [];

  const sample = rows.slice(0, FIELD_SAMPLE_ROWS);
  const keys = [];
  sample.forEach((row) => {
    if (!row || typeof row !== "object") return;
    Object.keys(row).forEach((key) => {
      if (keys.indexOf(key) === -1) keys.push(key);
    });
  });

  const fields = [];
  keys.forEach((key) => {
    let value;
    for (let i = 0; i < sample.length; i++) {
      const candidate = sample[i] ? sample[i][key] : undefined;
      if (candidate !== null && candidate !== undefined) {
        value = candidate;
        break;
      }
    }

    // undefined here means every sampled row was null/absent — keep the column.
    const isRenderable =
      value === undefined ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      (includeArrays && Array.isArray(value));
    if (!isRenderable) return;

    fields.push({
      id: fields.length,
      fieldName: key,
      checked: true,
      dataType: value === undefined ? "string" : Array.isArray(value) ? "array" : typeof value,
    });
  });

  return fields;
};

/**
 * Carries saved column widths onto a freshly derived field list.
 *
 * deriveAvailableFields builds brand-new field objects holding only
 * id/fieldName/checked/dataType, and the reducers below call it whenever a
 * widget's data source is picked or refreshed. Everything else configured per
 * field is therefore dropped at that moment - which is already true today of
 * fieldDisplayName, format and decimalPoints.
 *
 * Column widths cannot afford that: they are set by dragging the table itself,
 * saved with the report, and read back by every consumer including the embed
 * package, so losing them on a refresh would look like the feature breaking.
 *
 * Deliberately narrow - it copies `width` and nothing else. Carrying the rest
 * across would be a defensible fix but a different one, with its own behaviour
 * change to test; this keeps the existing loss exactly as it is.
 *
 * Matched on fieldName because `id` is an array index (see above) and moves when
 * the field list is reordered.
 */
export const carryFieldWidths = (freshFields, savedFields) => {
  if (!Array.isArray(freshFields) || !freshFields.length) return freshFields;
  if (!Array.isArray(savedFields) || !savedFields.length) return freshFields;

  const widthByName = new Map();
  savedFields.forEach((field) => {
    if (field && field.fieldName != null && Number(field.width) > 0) {
      widthByName.set(field.fieldName, Number(field.width));
    }
  });
  if (!widthByName.size) return freshFields;

  /* Fills a gap, never overwrites: a field that already arrives with a width keeps
     it. Otherwise a caller passing an updated field list (modifyResourceSource can
     be handed one) would have its new widths clobbered by the old ones. */
  return freshFields.map((field) => {
    if (!field || Number(field.width) > 0) return field;
    if (!widthByName.has(field.fieldName)) return field;
    return { ...field, width: widthByName.get(field.fieldName) };
  });
};

/* Display properties that belong to a single widget rather than to the whole board.
   Refresh and auto-refresh are deliberately NOT here: both reload the page, so scoping
   them to one widget on a dashboard would be meaningless. They stay board-level.
   A report holds exactly one widget, so a report never uses these - its board-level
   properties already describe that single widget. */
export const WIDGET_PROPERTY_DEFAULTS = { csvdownload: false, fullscreen: false };

export const normalizeWidgetProperties = (properties) => ({
  ...WIDGET_PROPERTY_DEFAULTS,
  ...(properties && typeof properties === "object" ? properties : {}),
});

export const saveDashboardAsync = createAsyncThunk("editor/saveDashboard", async (payload, { getState }) => {
  const { editor } = getState();
  const resourceList = editor.resourceList;
  console.log("resourceList ====>>>", resourceList);
  const resourceSavingData = resourceList.map((rs) => {
    return {
      connectionData:
        rs.sources && rs.sources.length > 0
          ? {
              sources: rs.sources.map((s) => {
                return {
                  id: s.resourceId || s.id,
                  uid: uuidv4(),
                };
              }),
            }
          : {
              id: rs.source.id,
              uid: uuidv4(),
            },
      id: rs.id,
      type: rs.type,
      widgetName: rs?.source?.widgetName,
      // Per-widget display properties. Always written, so a board saved before this
      // existed picks up the defaults the next time it is saved.
      properties: normalizeWidgetProperties(rs?.properties),
      availableFields: rs.availableFields,
      joins: rs.joins ? rs.joins : null,
      availableFilters: {},
      layout: rs.layout,
      dataFilters: rs?.dataFilters ? rs.dataFilters : [],
      aggregation: rs?.aggregation ? rs.aggregation : null,
      aggGroupBuilders: rs.aggGroupBuilders ? rs.aggGroupBuilders : null,
      // groupedReportData: rs?.groupedReportData ? rs.groupedReportData : null,
      aggRowBuilders: rs?.aggRowBuilders ? rs.aggRowBuilders : [],
      customFilters: rs?.customFilters ? rs.customFilters : [],
      /* PRM-517. How the reader's date control is drawn - merged range or separate
         From/To - plus the period names. Display only; the filters themselves are
         unchanged. witmeg-reporting-embed-v2 reads this to know which to render. */
      dateFilterDisplay: rs?.dateFilterDisplay ? rs.dateFilterDisplay : null,
      charts: rs?.charts ? rs.charts : {},
      allFields: rs?.allFields ? rs.allFields : [],
      drillDowns: rs?.drillDowns ? rs.drillDowns : [],
    };
  });
  // console.log(
  //   "saveDashboardAsync resourceSavingData===>>",
  //   resourceSavingData
  // );

  const reqBody = {
    userID: localStorage.getItem("userID"),
    orgID: localStorage.getItem("orgID"),
    appID: localStorage.getItem("appID"),
    isSpecialUser: localStorage.getItem("IsSpecialUser"),
    publish: payload.publish,
    name: editor.dashboardConfig.boardName,
    resources: resourceSavingData,
    configDetails: editor.dashboardConfig,
    properties: payload.properties,
    appList: editor.dashboardConfig.appList,
    id: payload.id,
  };
  const requestOptions = {
    method: "POST",
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify(reqBody),
  };
  const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/dashboard`, requestOptions);
  // .then(resp => {
  // })
  const data = await response.json();
  return data;
});

export const fetchReportDataAsync = createAsyncThunk("editor/details", async ({ selectedResource, isCombined }) => {
  // console.log(" fetchReportData===>>>>", resources.selectedResource)
  if (selectedResource && selectedResource.source) {
    // const response = await axios.get(resources.selectedResource.connectionData.baseUrl);
    // console.log(" fetchReportData===>>>>", selectedResource.source)
    try {
      const requestValues = selectedResource.source.connectionData.requestValues;
      const authInfo =
        selectedResource && selectedResource.source.connectionData && selectedResource.source.connectionData.authInfo;
      let urlParamSring = "";
      if (requestValues.urlParams.length > 0) {
        urlParamSring = urlParamSring + "?";
        for (let i = 0; i < requestValues.urlParams.length; i++) {
          if (requestValues.urlParams[i]["key"] !== "" && requestValues.urlParams[i]["value"] !== "") {
            urlParamSring =
              urlParamSring + requestValues.urlParams[i]["key"] + "=" + requestValues.urlParams[i]["value"] + "&";
          }
        }
        urlParamSring.slice(0, -1);
      }

      let headerObj = {};
      if (requestValues.requestHeaders.length > 0) {
        for (let i = 0; i < requestValues.requestHeaders.length; i++) {
          if (requestValues.requestHeaders[i]["key"] !== "" && requestValues.requestHeaders[i]["value"] !== "") {
            headerObj[requestValues.requestHeaders[i]["key"]] = requestValues.requestHeaders[i]["value"];
          }
        }
      }

      let bodyParamObj = {};
      // if (requestValues.authParams) {
      //     bodyParamObj = Object.assign({}, authParams);
      // }
      if (requestValues.bodyParams.length > 0) {
        for (let i = 0; i < requestValues.bodyParams.length; i++) {
          if (requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["value"] !== "") {
            bodyParamObj[requestValues.bodyParams[i]["key"]] = requestValues.bodyParams[i]["value"];
          } else if (requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["dataType"] === "date") {
            if (requestValues.bodyParams[i]["dateConfig"]) {
              let dateCf = requestValues.bodyParams[i]["dateConfig"];
              if (dateCf.dateOption === "daysBefore") {
                let dateVal = moment.utc().subtract(dateCf.timeUnits, "day").startOf("day").format(dateCf.dateFormat);
                bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
              } else if (dateCf.dateOption === "today") {
                let dateVal = moment().format(dateCf.dateFormat);
                bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
              }
            }
          }
        }
      }
      // // await Axios.get(resources.selectedResource.connectionData.baseUrl)
      if (authInfo.method === "jwt") {
        const timestamp = Math.floor(Date.now() / 1000) + 1000;
        const privateKey = authInfo.parameters.privateKey;
        var payload = {
          aud: authInfo.parameters.aud,
          exp: timestamp,
          iss: authInfo.parameters.iss,
          usertoken: localStorage.getItem("accessToken"),
        };
        var signOptions = {
          algorithm: "RS256",
        };

        var privateKeyVal = privateKey ? privateKey.replace(/\\n/g, "\n") : null;

        if (privateKeyVal) {
          try {
            var token = jwt.sign(payload, privateKeyVal, signOptions);
            // setApiTestStatus("success")

            return await Axios({
              method: requestValues.httpMethod,
              url: selectedResource.source.connectionData.baseUrl + urlParamSring,
              data: JSON.parse(JSON.stringify(bodyParamObj)),
              headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                Authorization: `Bearer ` + token,
              },
            })
              .then((response) => {
                const reportData = response.data ? (isCombined ? response.data.Result : response.data) : response;
                return reportData;
              })
              .catch((error) => {
                return [];
              });
          } catch (e) {
            return [];
          }
        }
      } else {
        let httpReqConfig = {
          method: selectedResource.source.connectionData.requestValues.httpMethod,
          url: selectedResource.source.connectionData.baseUrl + urlParamSring,
        };

        if (requestValues.bodyParams.length > 0) {
          httpReqConfig.data = bodyParamObj;
        }
        if (requestValues.requestHeaders.length > 0) {
          httpReqConfig.headers = headerObj;
        }
        return Axios(httpReqConfig)
          .then((response) => {
            // console.log(response);
            // setReportDataObject(response.data ? response.data : response)
            const reportData = response.data ? (isCombined ? response.data.Result : response.data) : response.Result;
            return reportData;
          })
          .catch((error) => {
            return [];
            // setReportDataObject(null)
            // console.log(error);
          });
      }
    } catch (error) {
      return []; // setReportDataObject(null)
      // console.log(error);
    }
  }
});

export const fetchAllResourceDataAsync = createAsyncThunk(
  "editor/fetchAllResourceData",
  async (_, { getState, dispatch }) => {
    const { editor } = getState();
    const resourceList = editor.resourceList;

    await Promise.all(
      resourceList.map(async (resource) => {
        // nothing to refresh until a connection has been picked
        if (!resource?.source?.connectionData) return;

        /* Ask for the data with the shape the editor already holds.
           This used to go through getReportData(), which rebuilds the argument as
           `source: resource.connectionData` - that key only exists on a board loaded from
           the API, not on an editor resource, so `source` came back undefined, the
           request was never made, and the widget was updated with reportData: undefined.
           That is why Refresh emptied the table instead of reloading it. */
        const response = await dispatch(fetchReportDataAsync({ selectedResource: resource }));
        const reportData = response?.payload;

        // a failed request must not wipe what is already on screen
        if (!reportData) return;

        dispatch(
          updateResourceSource({
            ...resource,
            reportData,
          }),
        );
      }),
    );
  },
);

export const getReportData = async (resource, dispatch, isCombined) => {
  const values = await dispatch(
    fetchReportDataAsync({
      selectedResource: { ...resource, source: !isCombined ? resource.connectionData : resource },
      isCombined,
    }),
  );
  return values.payload;
};

const getCombinedResourceData = async (resource, dispatch, resources, restApiResource) => {
  const sourceReportData = await getSources(resource, dispatch);

  const getSelectedApiResource = sourceReportData.map((item) => {
    return {
      id: item.id,
      name: item.name,
      resourceId: item.id,
      // selectedField: item.selectedField ? item.selectedField : null,
      // source: item.source ? item.source : null,
      reportData: item.reportData,
    };
  });

  const joins = (resource?.joins || []).map((join) => {
    return {
      id: join.id,
      resource1Id: join?.resource1Id || null,
      field1: join?.field1 || null,
      resource2Id: join?.resource2Id || null,
      field2: join?.field2 || null,
    };
  });

  // A join can point at a resource that has since been deleted, in which case
  // .find() returns undefined. Reading .name off that threw and left the editor
  // loading nothing at all, so fall back to the id as the column prefix.
  const prefixFor = (resourceId) => {
    const match = (restApiResource?.payload?.data || []).find((r) => r.id === resourceId);
    return String(match?.name || resourceId || "resource").replace(/\s/g, "_");
  };

  let reportArr = [];
  joins.forEach((li) => {
    let sets = [];
    const rows1 = getSelectedApiResource.find((r1) => r1.resourceId === li.resource1Id)?.reportData?.data || [];
    const rows2 = getSelectedApiResource.find((r2) => r2.resourceId === li.resource2Id)?.reportData?.data || [];
    const prefix1 = prefixFor(li.resource1Id);
    const prefix2 = prefixFor(li.resource2Id);

    rows1.forEach((rd1) => {
      rows2.forEach((rd2) => {
        if (rd1[li.field1] === rd2[li.field2]) {
          let oneData = {};
          let twoData = {};
          Object.keys(rd1).forEach((od) => {
            oneData[prefix1 + "_" + od] = rd1[od];
          });
          Object.keys(rd2).forEach((td) => {
            twoData[prefix2 + "_" + td] = rd2[td];
          });
          sets.push(Object.assign(oneData, twoData));
        }
      });
    });
    reportArr = _.merge(reportArr, sets);
  });
  return reportArr;
};

const getSources = async (resource, dispatch) => {
  return await Promise.all(
    (resource?.connectionData?.sources || []).map(async (source) => ({
      ...source,
      reportData: { data: await getReportData(source, dispatch, true) },
    })),
  );
};

/* ── TYPE COLLISION (fixed) ──
   This was declared as "boards/getBoardById" — the exact same action type boardSlice's
   getBoardByIdAsync uses. Action types are global, so createAsyncThunk built the same
   "boards/getBoardById/fulfilled" for both, and the extraReducer below ran for BOTH.

   Opening the board VIEW page dispatches boardSlice's thunk, whose payload is
   { data: {...} } with no selectedResources — so this slice quietly set resourceList,
   dashboardConfig and selectedResource to undefined. Nothing showed it at the time.
   Clicking Edit from that page then landed in the editor with resourceList undefined,
   and the "start with one resource" effect called addResouece, which read
   state.resourceList.length and threw. Reloading appeared to fix it because a reload
   rebuilds the store with the initial []. */
export const getDashboardDetailsAsync = createAsyncThunk("editor/getDashboardDetails", async (payload, { dispatch }) => {
  const restApiResource = await dispatch(getResourcesAsync());
  const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/dashboard-details/${payload.id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
  });
  const data = await response.json();
  // A non-200 / different envelope used to throw on `.resources.map` and the editor
  // then opened completely empty with no explanation.
  const boardResources = data?.data?.resources;
  if (!Array.isArray(boardResources)) {
    throw new Error(data?.message || "This report could not be loaded. Please try again.");
  }

  const selectedResources = await Promise.all(
    boardResources.map(async (resource, index) => {
      // A resource saved without connectionData is treated as a single source rather
      // than crashing on `.id`.
      const isSingleSource = Boolean(resource?.connectionData?.id);
      const reportData = isSingleSource
        ? await getReportData(resource, dispatch)
        : await getCombinedResourceData(resource, dispatch, payload.resources, restApiResource);

      // Boards saved while the column list was empty (the old refresh could wipe it)
      // have nothing to render in the Columns panel even though their data loads fine.
      // Rebuild the list from the response in that case so those reports self-heal.
      const savedFields = Array.isArray(resource.availableFields) ? resource.availableFields : [];
      const availableFields = savedFields.length
        ? savedFields
        : deriveAvailableFields(reportData, { includeArrays: true });

      return {
        ...resource,
        id: index + 1,
        availableFields,
        dataFilters: resource.dataFilters || [],
        aggregation: resource.aggregation || null,
        aggGroupBuilders: resource.aggGroupBuilders || null,
        properties: normalizeWidgetProperties(resource.properties),
        /* widgetName is saved at the resource root but every panel reads it from
           `source`, and `source` is rebuilt from connectionData - which carries only
           {id, uid}. A combined resource got no `source` at all. Either way reopening a
           saved dashboard showed the widget names blank, and retyping one was the only
           way to get it back. Rebuild it here for both shapes. */
        ...(isSingleSource
          ? { source: { ...resource.connectionData, widgetName: resource.widgetName } }
          : {
              source: { widgetName: resource.widgetName },
              sources: await Promise.all(
                (resource?.connectionData?.sources || []).map(async (source) => ({
                  ...source,
                  reportData: { data: await getReportData(source, dispatch, true) },
                })),
              ),
            }),
        widjet: resource.charts && Boolean(Object.values(resource.charts).length) ? "chart" : "table",
        reportData,
      };
    }),
  );

  return {
    selectedResources: selectedResources,
    dashboardConfig: data?.data?.configDetails,
    properties: data?.data?.properties,
    selectedResource: Boolean(selectedResources.length) ? selectedResources[0] : {},
  };
});

export const publishDashboardAsync = createAsyncThunk("editor/publishDashboard", async (payload) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify({
      id: payload.id,
      publish: payload.publish,
    }),
  };
  const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/publishdashboard`, requestOptions);
  // .then(resp => {
  // })
  const data = await response.json();
  // console.log("SAVE RESPONSE====>>", data)
  return data;
});

export const editorSlice = createSlice({
  name: "editor",
  initialState: {
    resourceList: [],
    resourceListStatus: "default",
    // selectedResource: {},
    selectedResource: null,
    dashboardConfig: null,
    properties: {},
    boardSavingState: { status: "DEFAULT" },
    boardPublishState: { status: "DEFAULT" },
    savedBoardId: null,
    deletedResourceID: null,
    drillDowns: [],
    isLoadingDashboard: false,
    dashboardLoadError: null,
    isRefreshing: false,
  },
  reducers: {
    addResouece: (state, action) => {
      // Read through the local copy below, never state.resourceList directly - the id
      // line used state.resourceList.length, which threw whenever the list was not an
      // array. A reducer that throws is not catchable by an error boundary: React
      // unmounts the tree and the page goes to "Something went wrong".
      let newResourceList = Array.isArray(state.resourceList) ? [...state.resourceList] : [];
      let max = _.maxBy(newResourceList, "id");

      /* Full shape up front: the panels read availableFields / dataFilters / charts and
         used to get undefined on a freshly added resource. */
      const newResource = {
        // newResourceList, not state.resourceList — optional chaining stops the throw but
        // hands back `undefined + 1` = NaN, and a resource with id NaN matches nothing
        id: max?.id ? max?.id + 1 : newResourceList.length + 1,
        type: action.payload,
        source: {},
        sources: [],
        joins: [],
        reportData: null,
        availableFields: [],
        allFields: [],
        charts: {},
        dataFilters: [],
        customFilters: [],
        aggRowBuilders: [],
        aggGroupBuilders: [],
        drillDowns: [],
        properties: { ...WIDGET_PROPERTY_DEFAULTS },
      };

      newResourceList.push(newResource);
      state.resourceList = newResourceList;
      /* Select what was actually created. The caller used to build its own object with a
         hardcoded id of 1 and select that, so adding a second resource left the panels
         pointing at the first one. */
      state.selectedResource = newResource;
      state.addedResourceId = newResource.id;
    },

    /* Changing a block's API Type rewrites that resource in place. Picking a type used to
       ADD another resource, which is wrong once the panel is a list of blocks. */
    setResourceType: (state, action) => {
      const { id, type } = action.payload;
      state.resourceList = state.resourceList.map((rl) =>
        rl.id === id ? { ...rl, type, source: {}, sources: [], joins: [], reportData: null } : rl,
      );
      if (state.selectedResource?.id === id) {
        state.selectedResource = state.resourceList.find((rl) => rl.id === id);
      }
    },
    removeResource: (state, action) => {
      let currentResourceList = [...state.resourceList];

      state.resourceList = currentResourceList.filter((cr) => cr.id !== action.payload.id);

      /* Fall back to a remaining resource instead of leaving nothing selected. The panels
         read selectedResource.reportData, so a null here crashed the editor the moment a
         resource was deleted - which is what the error boundary was catching. */
      // state.selectedResource = {};
      // state.selectedResource = null;
      state.selectedResource = state.resourceList.length ? state.resourceList[0] : null;
      state.deletedResourceID = action.payload.id;
    },
    resetResourceRemoveState: (state) => {
      state.deletedResourceID = null;
    },
    resetResourceAddStatus: (state) => {
      state.addedResourceId = null;
    },
    addResoueceList: (state, action) => {
      // normalized so a resource added through this path still has display properties -
      // the panel reads them straight off the resource
      let newResourceList = [
        ...state.resourceList,
        ...action.payload.map((r) => ({ ...r, properties: normalizeWidgetProperties(r?.properties) })),
      ];
      // newResourceList.push({ id: (state.resourceList.length + 1), type: action.payload, source: {} })
      state.resourceList = newResourceList;
    },
    deleteResource: (state, action) => {
      state.resourceList = [...state.resourceList].filter((r) => r.id !== action.payload.id);
    },
    updateReportData: (state, action) => {
      let updateResList = [...state.resourceList];

      const resource = updateResList.map((resource) => {
        if (resource.id === action.payload.id) {
          return {
            ...resource,
            reportData: action.payload.reportData,
            sources: action.payload.sources,
          };
        }
        return resource;
      });

      state.resourceList = resource;
      state.selectedResource = Object.assign({}, state.selectedResource, {
        reportData: action.payload.reportData,
        sources: action.payload.sources,
      });
    },
    /* Clears a resource's chart config. Without an id it clears the selected one, which
       is the original behaviour. With an id it clears that resource - used when a chart
       widget is deleted from the canvas, otherwise the config stayed behind and the next
       chart dropped on that resource silently rendered the deleted chart instead of
       saying it is not configured yet. */
    resetChartConfig: (state, action) => {
      const targetId = action?.payload?.id ?? state.selectedResource?.id;
      if (targetId === undefined || targetId === null) return;

      if (state.selectedResource?.id === targetId) {
        state.selectedResource.charts = {};
      }

      // ── also clear in resourceList ──
      const index = state.resourceList.findIndex((r) => r.id === targetId);
      if (index !== -1) {
        state.resourceList[index].charts = {};
      }
    },

    updateResourceSource: (state, action) => {
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === action.payload.id);
      if (updateIndex === -1) return;
      // Columns are derived from the data, so a failed request (getReportData returns
      // [] on every error) used to look identical to an empty table and overwrote the
      // saved config with []. Saving after that wrote the board back with no columns.
      // Keep whatever was already configured unless we genuinely have fresh data.
      // Plain copies — these come out of the Immer draft we are about to replace, so
      // they must not stay as proxies once the old draft leaves the state tree.
      const savedFields = (updateResList[updateIndex].availableFields || []).map((f) => ({ ...f }));
      const savedAllFields = (updateResList[updateIndex].allFields || []).map((f) => ({ ...f }));
      /* ...but only while the source stays the same. Picking a DIFFERENT source in the
         dropdown reaches this reducer looking exactly like a failed fetch when the new
         source returns nothing, so the fallback above kept the previous source's columns
         on screen - and they were not a stale render, they were written back into the
         resource, so a save would have stored one source's data with another's columns.

         Read before the assignment below replaces the whole resource object. Treated as
         a change only when both ids are present and differ: an id we cannot see is not
         evidence of a swap, and must not be allowed to wipe a configured column list. */
      const previousSourceId = updateResList[updateIndex]?.source?.id;
      const incomingSourceId = action.payload?.source?.id;
      const sourceChanged = previousSourceId != null && incomingSourceId != null && previousSourceId !== incomingSourceId;
      // Display properties are set in the right-hand panel, not by the resource picker,
      // so swapping the source must not reset them.
      const savedProperties = normalizeWidgetProperties(updateResList[updateIndex].properties);
      //  console.log("updateIndex===>>", updateIndex)
      updateResList[updateIndex] = action.payload;
      updateResList[updateIndex]["properties"] = normalizeWidgetProperties(
        action.payload.properties || savedProperties,
      );
      // ── OLD DERIVATION (kept for revert) ──
      // Read only data[0] and kept string/number/array values, which lost sparse and
      // boolean columns. Replaced by deriveAvailableFields() above.
      // updateResList[updateIndex]["availableFields"] =
      // action.payload.reportData &&
      // (action.payload.reportData.length > 0 || Object.keys(action.payload.reportData)) &&
      // returnDataArrayObject(action.payload.reportData).data &&
      // returnDataArrayObject(action.payload.reportData).data.length
      // ? Object.keys(returnDataArrayObject(action.payload.reportData).data[0])
      // .filter(
      // (fl) =>
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "string" ||
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "number" ||
      // Array.isArray(returnDataArrayObject(action.payload.reportData).data[0][fl]) === true,
      // )
      // .map((ok, i) => {
      // return {
      // id: i,
      // fieldName: ok,
      // checked: true,
      // dataType: typeof returnDataArrayObject(action.payload.reportData).data[0][ok],
      // };
      // })
      // : savedFields && savedFields.length
      // ? savedFields // keep the configured columns; a failed/empty fetch must not erase them
      // : [];
      // updateResList[updateIndex]["allFields"] =
      // action.payload.reportData &&
      // (action.payload.reportData.length > 0 || Object.keys(action.payload.reportData)) &&
      // returnDataArrayObject(action.payload.reportData).data &&
      // returnDataArrayObject(action.payload.reportData).data.length
      // ? Object.keys(returnDataArrayObject(action.payload.reportData).data[0])
      // .filter(
      // (fl) =>
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "string" ||
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "number",
      // )
      // .map((ok, i) => {
      // return {
      // id: i,
      // fieldName: ok,
      // checked: true,
      // dataType: typeof returnDataArrayObject(action.payload.reportData).data[0][ok],
      // };
      // })
      // : savedAllFields && savedAllFields.length
      // ? savedAllFields
      // : [];

      const freshFields = deriveAvailableFields(action.payload.reportData, { includeArrays: true });
      const freshAllFields = deriveAvailableFields(action.payload.reportData);
      /* Fresh data wins. Otherwise keep what was configured rather than wiping it -
         unless the source just changed, in which case the old columns describe data
         this resource no longer has, and an empty list is the honest answer.

         Widths follow the same rule: they are matched by fieldName, so carrying them
         across a swap would silently size a new source's column from an unrelated
         field that happened to share a name. */
      updateResList[updateIndex]["availableFields"] = freshFields.length
        ? carryFieldWidths(freshFields, sourceChanged ? [] : savedFields)
        : sourceChanged
          ? []
          : savedFields;
      updateResList[updateIndex]["allFields"] = freshAllFields.length
        ? freshAllFields
        : sourceChanged
          ? []
          : savedAllFields;

      console.log("updateResList ====>>>", updateResList);
      state.resourceList = updateResList;
      state.selectedResource = action.payload;
    },

    modifyResourceSource: (state, action) => {
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === action.payload.id);
      if (updateIndex === -1) return;
      // see updateResourceSource — the picker must not reset the display properties
      const savedProperties = normalizeWidgetProperties(updateResList[updateIndex].properties);
      // Read before the assignment below replaces the whole resource object. Plain
      // copies, so they do not stay as proxies into the draft we are discarding.
      const savedFieldWidths = (updateResList[updateIndex].availableFields || []).map((f) => ({ ...f }));
      //  console.log("updateIndex===>>", updateIndex)
      updateResList[updateIndex] = action.payload;
      updateResList[updateIndex]["properties"] = normalizeWidgetProperties(
        action.payload.properties || savedProperties,
      );
      // ── OLD DERIVATION (kept for revert) — see deriveAvailableFields() ──
      // updateResList[updateIndex]["availableFields"] = action.payload.availableFields
      // ? action.payload.availableFields
      // : action.payload.reportData &&
      // (action.payload.reportData.length > 0 || Object.keys(action.payload.reportData)) &&
      // returnDataArrayObject(action.payload.reportData).data &&
      // returnDataArrayObject(action.payload.reportData).data.length
      // ? Object.keys(returnDataArrayObject(action.payload.reportData).data[0])
      // .filter(
      // (fl) =>
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "string" ||
      // typeof returnDataArrayObject(action.payload.reportData).data[0][fl] === "number",
      // )
      // .map((ok, i) => {
      // return {
      // id: i,
      // fieldName: ok,
      // checked: true,
      // dataType: typeof returnDataArrayObject(action.payload.reportData).data[0][ok],
      // };
      // })
      // : [];

      updateResList[updateIndex]["availableFields"] = carryFieldWidths(
        action.payload.availableFields && action.payload.availableFields.length
          ? action.payload.availableFields
          : deriveAvailableFields(action.payload.reportData),
        savedFieldWidths,
      );
      state.resourceList = updateResList;
      state.selectedResource = action.payload;
      updateResList[updateIndex]["reportData"] = action.payload.reportData ? action.payload.reportData : null;
    },
    updateResourceSourceAndFilter: (state, action) => {
      //console.log("updateResourceSourceAndFilter  <<>> action===>>", action)
      let updateResList = [...state.resourceList];

      let updateIndex = updateResList.findIndex((ur) => ur.id === action.payload.selectedResource.id);
      if (updateIndex === -1) return;
      // // updateResList[updateIndex] = action.payload.selectedResource

      // updateResList[updateIndex]["reportData"] = action.payload.reportData;
      // if(updateResList[updateIndex]["availableFields"].length===0) {
      // updateResList[updateIndex]["availableFields"] =
      //   action.payload.reportData &&
      //   (action.payload.reportData.length > 0 ||
      //     Object.keys(action.payload.reportData)) &&
      //   returnDataArrayObject(action.payload.reportData).data &&
      //   returnDataArrayObject(action.payload.reportData).data.length
      //     ? Object.keys(
      //         returnDataArrayObject(action.payload.reportData).data[0]
      //       )
      //         .filter(
      //           (fl) =>
      //             typeof returnDataArrayObject(action.payload.reportData)
      //               .data[0][fl] === "string" ||
      //             typeof returnDataArrayObject(action.payload.reportData)
      //               .data[0][fl] === "number"
      //         )
      //         .map((ok, i) => {
      //           return {
      //             id: i,
      //             fieldName: ok,
      //             checked: true,
      //             dataType: typeof returnDataArrayObject(
      //               action.payload.reportData
      //             ).data[0][ok],
      //           };
      //         })
      //     : [];
      // }
      updateResList[updateIndex]["dataFilters"] = action.payload.dataFilters;
      updateResList[updateIndex]["customFilters"] = action.payload.customFilters;
      /* PRM-517. Guarded rather than assigned straight: a caller that does not know
         about the date display must not blank out what the author already chose. */
      if (action.payload.dateFilterDisplay !== undefined) {
        updateResList[updateIndex]["dateFilterDisplay"] = action.payload.dateFilterDisplay;
      }

      state.resourceList = updateResList;

      let selected = Object.assign(updateResList[updateIndex]);

      // selected.dataFilters = action.payload.dataFilters
      state.selectedResource = selected;

      // console.log("updateDataFilters action ====>>>", action);
      // console.log("updateDataFilters state ====>>>", state);
      // let selected = Object.assign(state.selectedResource);
      // let reportRowData = returnDataArrayObject(selected['reportData'])
      // console.log("selected ====>>>", selected);
      // console.log("reportRowData ====>>>", reportRowData);
      // selected.dataFilters = action.payload.dataFilters
      // state.selectedResource = selected
      // let updateResList = [...state.resourceList];
      // let updateIndex = updateResList.findIndex(ur => ur.id === selected.id)
      // updateResList[updateIndex]['dataFilters'] = action.payload.dataFilters
    },
    // updateSelectedResource: (state, action) => {
    //   state.selectedResource = {
    //     id: action.payload.id,
    //     type: action.payload.type,
    //     source: action.payload.source ? action.payload.source : {},
    //     sources: action.payload?.sources || [],
    //     joins: action.payload?.joins || [],
    //     reportData: action.payload.reportData ? action.payload.reportData : null,
    //     availableFields: action.payload.availableFields ? action.payload.availableFields : [], //action.payload.reportData && (action.payload.reportData.length > 0 || Object.keys(action.payload.reportData)) && returnDataArrayObject(action.payload.reportDataObject).data && returnDataArrayObject(action.payload.reportDataObject).data.length ? Object.keys(returnDataArrayObject(action.payload.reportDataObject).data[0]) : []
    //     allFields: action.payload.availableFields ? action.payload.availableFields : [],
    //     charts: action.payload?.charts || {},
    //     dataFilters: action.payload?.dataFilters || [],
    //     customFilters: action.payload?.customFilters || [],
    //     layout: action.payload?.layout,
    //     aggRowBuilders: action.payload?.aggRowBuilders || [],
    //     aggregation: {
    //       row: action.payload?.aggregation?.row || [],
    //     },
    //     aggGroupBuilders: action.payload?.aggGroupBuilders || [],
    //   };
    // },
    updateSelectedResource: (state, action) => {
      if (!action.payload) {
        state.selectedResource = null;
        return;
      }

      state.selectedResource = {
        id: action.payload?.id,
        type: action.payload?.type,
        source: action.payload?.source || {},
        sources: action.payload?.sources || [],
        joins: action.payload?.joins || [],
        reportData: action.payload?.reportData || null,
        availableFields: action.payload?.availableFields || [],
        allFields: action.payload?.allFields || [],
        charts: action.payload?.charts || {},
        dataFilters: action.payload?.dataFilters || [],
        customFilters: action.payload?.customFilters || [],
        layout: action.payload?.layout || null,
        aggRowBuilders: action.payload?.aggRowBuilders || [],
        aggregation: action.payload?.aggregation || { row: [] },
        aggGroupBuilders: action.payload?.aggGroupBuilders || [],
      };
    },
    updateSelectedResourceWidgetName: (state, action) => {
      // alert("SSSSS")
      const updatedResourceList = state.resourceList.map((rl) => {
        return {
          ...rl,
          source:
            rl.id === action.payload.id ? { ...rl.source, widgetName: action.payload.widgetName } : { ...rl.source },
        };
      });
      state.resourceList = updatedResourceList;
      state.selectedResource = {
        ...state.selectedResource,
        source: {
          ...state.selectedResource.source,
          widgetName: action.payload.widgetName,
        },
      };
    },

    /* Display properties for ONE widget. The board-level equivalents live on
       state.properties and are owned by the editor component; these are per resource so
       a dashboard can allow download on one widget and not another. */
    updateResourceProperties: (state, action) => {
      const { id, key, value } = action.payload;
      const updateIndex = state.resourceList.findIndex((rl) => rl.id === id);
      if (updateIndex === -1) return;

      const next = { ...normalizeWidgetProperties(state.resourceList[updateIndex].properties), [key]: value };
      state.resourceList[updateIndex].properties = next;
      if (state.selectedResource?.id === id) {
        state.selectedResource = { ...state.selectedResource, properties: next };
      }
    },

    /* ── COLUMN WIDTH ──
       Written when the user finishes dragging a column edge in the editor's table
       preview (once per drag, on release - see ReportGrid / ResizableTitle). Lands
       on the field as `width`, so it travels to the API inside availableFields with
       no change to the save payload, and every consumer that reads availableFields -
       the board view, the report page, the embed package - gets it for free.

       Payload: { resourceId, fieldName, width }.

       Written this way rather than through updateSelectedFields because that one
       REPLACES the whole field object with its payload, so a partial write silently
       drops format / fieldDisplayName / checked; and it writes to selectedResource,
       which on a multi-widget canvas is not necessarily the widget being dragged.
       This follows updateWidgetProperty instead: address the resource by id, merge
       one key. */
    setFieldWidth: (state, action) => {
      const { resourceId, fieldName, width } = action.payload || {};
      if (fieldName == null) return;

      const nextWidth = Math.round(Number(width));
      if (!(nextWidth > 0)) return;

      const applyTo = (resource) => {
        if (!resource || !Array.isArray(resource.availableFields)) return false;
        const fieldIndex = resource.availableFields.findIndex((f) => f && f.fieldName === fieldName);
        if (fieldIndex === -1) return false;
        resource.availableFields[fieldIndex].width = nextWidth;
        return true;
      };

      const updateIndex = state.resourceList.findIndex((rl) => rl.id === resourceId);
      if (updateIndex !== -1) applyTo(state.resourceList[updateIndex]);

      /* The panels read the selected resource rather than the list, so it has to be
         written too - the same shape as every other reducer here. */
      if (state.selectedResource && state.selectedResource.id === resourceId) {
        applyTo(state.selectedResource);
      }
    },

    updateSelectedFields: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      // selected.availableFields = [...selected.availableFields.filter(af => af.id !== action.payload.id), ...[action.payload]]
      selected.availableFields[selected.availableFields.findIndex((s) => s.id === action.payload.id)] = action.payload;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["availableFields"][
        updateResList[updateIndex]["availableFields"].findIndex((s) => s.id === action.payload.id)
      ] = action.payload;
      state.resourceList = updateResList;
    },
    // updateAllFields: (state, action) => {
    //   console.log("updateSelectedFields Action==>>", action);
    //   let selected = Object.assign(state.selectedResource);
    //   selected.allFields = [
    //     ...selected.allFields,
    //     ...action.payload?.map((a) => ({
    //       id: uuidv4(),
    //       fieldName: a.displayName,
    //       dataType: a?.dataType ?? "number",
    //       isAggrCol: true,
    //       grouped: true,
    //       className: "aggr-col",
    //     })),
    //   ];
    //   state.selectedResource = selected;
    //   let updateResList = [...state.resourceList];
    //   let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
    //   updateResList[updateIndex]["allFields"] = [
    //     ...updateResList[updateIndex]["allFields"],
    //     ...action.payload?.map((a) => ({
    //       id: uuidv4(),
    //       fieldName: a.displayName,
    //       dataType: a?.dataType ?? "number",
    //       isAggrCol: true,
    //       grouped: true,
    //       className: "aggr-col",
    //     })),
    //   ];
    //   state.resourceList = updateResList;
    // },
    resetAggBuilders: (state) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      selected.aggRowBuilders = [];
      selected.aggregation = {};
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["aggRowBuilders"] = [];
      updateResList[updateIndex]["aggregation"] = {};
      state.resourceList = updateResList;
    },
    resetGroupBuilders: (state) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      selected.aggGroupBuilders = null;
      selected.groupedReportData = [];
      selected.aggRowBuilders = null;
      selected.aggregation = null;
      selected.availableFields = [...selected.allFields];
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["aggGroupBuilders"] = null;
      updateResList[updateIndex]["groupedReportData"] = [];
      updateResList[updateIndex]["aggRowBuilders"] = null;
      updateResList[updateIndex]["aggregation"] = null;
      updateResList[updateIndex]["availableFields"] = [...updateResList[updateIndex].allFields];
      state.resourceList = updateResList;
      state.selectedResource = selected;
    },
    updateGroupedSelectedFields: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      // selected.availableFields = [...selected.availableFields.filter(af => af.id !== action.payload.id), ...[action.payload]]
      selected.availableFields = [...action.payload];
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["availableFields"] = action.payload;
      state.resourceList = updateResList;
    },
    updateGroupedReportData: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);

      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["availableFields"] = action.payload.fields;
      updateResList[updateIndex]["groupedReportData"] = action.payload.data;
      state.resourceList = updateResList;

      // selected.availableFields = [...selected.availableFields.filter(af => af.id !== action.payload.id), ...[action.payload]]
      selected.availableFields = action.payload.fields;
      selected["groupedReportData"] = action.payload.data;
      // selected["groupedReportData"] = [...action.payload.data];
      state.selectedResource = selected;
    },
    reOrderFields: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      selected.availableFields = action.payload;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["availableFields"] = action.payload;
    },
    updateComponentPositions: (state, action) => {
      if (!Array.isArray(state.resourceList)) return;
      const resList = [...state.resourceList];
      const layout = action.payload.layout;
      if (action.payload.layout && action.payload.layout.length > 0) {
        /* Match each resource to ITS grid item by id.
           This read layout[index] - the grid item at the same position in the array.
           resourceList is ordered by when resources were added; the layout array is
           ordered by the grid, and RGL reorders it as items move. With one widget the
           two lined up by luck; with two or more, moving one could write its position
           onto the other, so widgets swapped places after a save and reload. */
        const updateResList = resList.map((item, index) => {
          const match =
            layout.find((l) => String(l.i) === String(item?.layout?.i)) ||
            // a resource whose widget has no saved `i` yet falls back to position, which
            // is what this always did
            layout[index];
          // nothing to write for a resource with no widget on the canvas
          if (!match) return item;

          return Object.assign(item, {
            layout: {
              x: match.x || 0,
              y: match.y || 0,
              h: match.h || 0,
              i: match.i || 0,
              w: match.w || 0,
            },
          });
        });
        state.resourceList = updateResList;
      }
    },
    updateDataFilters: (state, action) => {
      //  console.log("updateDataFilters action ====>>>", action);

      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let reportRowData = returnDataArrayObject(selected["reportData"]);
      //  console.log("selected ====>>>", selected);
      //  console.log("reportRowData ====>>>", reportRowData);
      selected.dataFilters = action.payload.dataFilters;
      selected.applyFilters = action.payload.applyFilters;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["dataFilters"] = action.payload.dataFilters;
      updateResList[updateIndex]["applyFilters"] = action.payload.applyFilters;
      // updateResList[updateIndex] = Object.assign(updateResList[updateIndex], { 'reportData': [...reportRowData].filter(item => item[action.payload[0]['fieldName']] === action.payload[0]['value']) })
    },

    updateSelectedWidjet: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;

      if (updateResList[updateIndex]["widjet"] && updateResList[updateIndex]["widjet"]) {
        updateResList[updateIndex]["widjet"] = action.payload;
      } else {
        updateResList[updateIndex]["widjet"] = action.payload;
      }

      if (selected["widjet"] && selected["widjet"]) {
        selected["widjet"] = action.payload;
      } else {
        selected["widjet"] = action.payload;
      }

      state.resourceList = updateResList;
      state.selectedResource = selected;
    },

    updateChart: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;

      if (updateResList[updateIndex]["charts"] && updateResList[updateIndex]["charts"]) {
        updateResList[updateIndex]["charts"] = action.payload;
      } else {
        updateResList[updateIndex]["charts"] = action.payload;
      }

      if (selected["charts"] && selected["charts"]) {
        selected["charts"] = action.payload;
      } else {
        selected["charts"] = action.payload;
      }

      state.resourceList = updateResList;
      state.selectedResource = selected;
    },

    updateAggGroupBuilders: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let currentAvailableFields = selected.availableFields;
      selected.aggGroupBuilders = action.payload;
      selected.availableFields = currentAvailableFields;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["aggGroupBuilders"] = action.payload;
      state.resourceList = updateResList;
    },

    updateAggRowBuilders: (state, action) => {
      //  console.log("updateAggRowBuilders state ====>>>", state);
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let reportRowData = returnDataArrayObject(selected["reportData"]);
      // console.log("selected ====>>>", selected);
      // console.log("reportRowData ====>>>", reportRowData);
      // console.log("AggRowData===>>", action.payload);
      selected.aggRowBuilders = action.payload;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["aggRowBuilders"] = action.payload;
      // updateResList[updateIndex] = Object.assign(updateResList[updateIndex], { 'reportData': [...reportRowData].filter(item => item[action.payload[0]['fieldName']] === action.payload[0]['value']) })
      state.resourceList = updateResList;
    },

    updateAggColBuilders: (state, action) => {
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      // selected.dataFilters = action.payload
      // state.selectedResource = selected
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      console.log("updateIndex ====>>>", updateIndex);
      if (updateResList[updateIndex]["aggregation"] && updateResList[updateIndex]["aggregation"]["row"]) {
        updateResList[updateIndex]["aggregation"]["row"] = action.payload;
      } else {
        updateResList[updateIndex]["aggregation"] = {
          row: action.payload,
        };
      }

      if (selected["aggregation"] && selected["aggregation"]["row"]) {
        selected["aggregation"]["row"] = action.payload;
      } else {
        selected["aggregation"] = {
          row: action.payload,
        };
      }
      // // updateResList[updateIndex] = Object.assign(updateResList[updateIndex], { 'reportData': [...reportRowData].filter(item => item[action.payload[0]['fieldName']] === action.payload[0]['value']) })
      state.resourceList = updateResList;
      state.selectedResource = selected;
    },
    addDashboardConfig: (state, action) => {
      //console.log("addDashboardConfig action====.>>>>", action)
      state.dashboardConfig = action.payload;
    },
    resetEditor: (state) => {
      //  console.log("action===updateSelectedResource==>>", action)
      state.selectedResource = null;
      state.resourceList = [];
      state.resourceListStatus = "default";
      // state.dashboardConfig = {}
    },
    resetAllEditorData: (state) => {
      state.selectedResource = null;
      state.resourceList = [];
      state.resourceListStatus = "default";
      state.dashboardConfig = null;
      state.boardSavingState = { status: "DEFAULT" };
      state.boardPublishState = { status: "DEFAULT" };
      state.savedBoardId = null;
    },
    resetBoardSavingState: (state) => {
      state.boardSavingState = { ...state.boardSavingState, status: "DEFAULT" };
    },
    resetBoardPublishState: (state) => {
      state.boardPublishState = { status: "DEFAULT" };
    },

    deleteFilters: (state, action) => {
      const { id, filterType } = action.payload;
      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;

      if (Boolean(updateResList[updateIndex][filterType]?.length)) {
        updateResList[updateIndex][filterType] = updateResList[updateIndex][filterType].filter((i) => i.id !== id);
      }

      if (Boolean(selected?.[filterType]?.length)) {
        selected[filterType] = selected[filterType].filter((i) => i.id !== id);
      }

      state.resourceList = updateResList;
      state.selectedResource = selected;
    },

    updateDashboardName: (state, action) => {
      let currentConfig = Object.assign(state.dashboardConfig);
      currentConfig.boardName = action.payload;
      state.dashboardConfig = currentConfig;
    },

    updateDrillDowns: (state, action) => {
      // state.drillDowns = action.payload

      // no resource selected - nothing to write to (see removeResource)
      if (!state.selectedResource) return;
      let selected = Object.assign(state.selectedResource);
      selected.drillDowns = action.payload;
      state.selectedResource = selected;
      let updateResList = [...state.resourceList];
      let updateIndex = updateResList.findIndex((ur) => ur.id === selected.id);
      if (updateIndex === -1) return;
      updateResList[updateIndex]["drillDowns"] = action.payload;
      state.resourceList = updateResList;
    },
  },
  extraReducers: {
    [saveDashboardAsync.pending]: (state) => {
      state.boardSavingState = { status: "PENDING" };
    },
    [saveDashboardAsync.fulfilled]: (state, action) => {
      if (action.payload.status === "BadRequest") {
        state.boardSavingState = { status: "BadRequest", ...action.payload };
      } else {
        state.boardSavingState = { status: "SUCCESS", ...action.payload };
        state.savedBoardId = action.payload.id;
      }
    },
    [saveDashboardAsync.rejected]: (state, action) => {
      state.boardSavingState = { status: "ERROR" };
    },
    [publishDashboardAsync.pending]: (state) => {
      state.boardPublishState = { status: "PENDING" };
    },
    [publishDashboardAsync.fulfilled]: (state, action) => {
      state.boardPublishState = { status: "SUCCESS", ...action.payload };
    },
    [publishDashboardAsync.rejected]: (state, action) => {
      state.boardPublishState = { status: "ERROR", ...action.payload };
    },
    [getDashboardDetailsAsync.pending]: (state) => {
      state.isLoadingDashboard = true;
      state.dashboardLoadError = null;
    },
    [getDashboardDetailsAsync.fulfilled]: (state, action) => {
      state.isLoadingDashboard = false;
      state.dashboardLoadError = null;
      /* Never let this leave resourceList as anything but an array. That is what the
         action-type collision did, and the crash it caused surfaced two screens later in
         addResouece, which made it look like an editor bug. */
      state.resourceList = Array.isArray(action.payload?.selectedResources) ? action.payload.selectedResources : [];
      state.dashboardConfig = action.payload?.dashboardConfig ?? null;
      state.selectedResource = action.payload?.selectedResource ?? null;
      // the editor reads this straight off, e.g. this.props.properties.filters
      state.properties = action.payload?.properties ?? {};
    },
    [getDashboardDetailsAsync.rejected]: (state, action) => {
      state.isLoadingDashboard = false;
      // Previously this only cleared the spinner, so a failed load looked like a board
      // with nothing in it. Record the reason so the editor can say what happened.
      state.dashboardLoadError = action.error?.message || "This report could not be loaded. Please try again.";
    },
    [fetchAllResourceDataAsync.pending]: (state) => {
      state.isRefreshing = true;
    },
    [fetchAllResourceDataAsync.fulfilled]: (state) => {
      state.isRefreshing = false;
    },
    [fetchAllResourceDataAsync.rejected]: (state) => {
      state.isRefreshing = false;
    },
  },
});

export const {
  addResouece,
  setResourceType,
  removeResource,
  resetResourceRemoveState,
  resetResourceAddStatus,
  addResoueceList,
  deleteResource,
  updateResourceSource,
  modifyResourceSource,
  updateResourceSourceAndFilter,
  updateSelectedResource,
  updateSelectedResourceWidgetName,
  updateResourceProperties,
  updateSelectedFields,
  setFieldWidth,
  updateGroupedSelectedFields,
  reOrderFields,
  updateComponentPositions,
  updateDataFilters,
  updateAggRowBuilders,
  updateAggColBuilders,
  addDashboardConfig,
  resetEditor,
  resetAllEditorData,
  updateChart,
  resetBoardSavingState,
  resetBoardPublishState,
  updateSelectedWidjet,
  deleteFilters,
  updateDashboardName,
  updateGroupedReportData,
  updateAggGroupBuilders,
  resetAggBuilders,
  resetGroupBuilders,
  updateReportData,
  updateDrillDowns,
  resetChartConfig,
  // updateAllFields,
} = editorSlice.actions;

export default editorSlice.reducer;
